import {
  isHuafendaDk108Device,
  isHuafendaDk108Wireless,
} from "../main/hfdDk108";
import { isHuafendaX75GcDevice } from "../main/hfdX75Gc";
import { getSupportedDevicesSync } from "../main/supportedDevices";
import { xingshanHidMatchesConfiguration } from "../main/keyboardDeviceMatch";

export function normalizeEquipmentName(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/** 用于华奋达等同 VID/PID 多型号的名称比对 */
export function equipmentNameBlob(
  equipment: Record<string, unknown> | null | undefined
): string {
  if (!equipment) return "";
  const dn = normalizeEquipmentName(
    (equipment as { displayname?: unknown }).displayname ??
      (equipment as { displayName?: unknown }).displayName
  );
  const pn = normalizeEquipmentName(equipment.productName);
  const ident = normalizeEquipmentName(equipment.identifier);
  return [dn, pn, ident].filter(Boolean).join(" ");
}

/**
 * 华奋达 DK108 有线：支持神光同步、全局律动。
 * 2.4G：不参与神光同步，走子应用 musicDance（setMusicDataV1，与 M98 同级）。
 */
export function isDk108GlobalRhythmSyncCapableEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment) return false;
  if (!isHuafendaDk108Device(equipment)) return false;
  return !isHuafendaDk108Wireless(equipment);
}

/** 华奋达 M98 屏幕版（M98TFT / displayname M98 Screen），2.4G 走全局律动而非 V1 */
export function isM98ScreenTftEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment) return false;
  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "M98TFT") return true;
  const dn = normalizeEquipmentName(
    (equipment as { displayname?: unknown }).displayname ??
      (equipment as { displayName?: unknown }).displayName
  );
  return dn === "m98 screen";
}

/** 华奋达 M98 8K（M98 8K / displayname M98 (8K) / MUSE-M98-8K） */
export function isM98_8KEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment) return false;
  if (isHuafendaX75GcDevice(equipment)) return false;
  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "M98 8K") return true;
  const dn = normalizeEquipmentName(
    (equipment as { displayname?: unknown }).displayname ??
      (equipment as { displayName?: unknown }).displayName
  );
  if (dn === "m98 (8k)") return true;
  const pn = normalizeEquipmentName(equipment.productName);
  if (pn === "muse-m98-8k") return true;
  const id = String(equipment.identifier ?? "")
    .trim()
    .toLowerCase();
  if (
    id.includes("65276") ||
    (id.includes("34819") && !id.includes("x75gc")) ||
    id.includes("muse-m98-8k")
  ) {
    return true;
  }
  const pid = Number((equipment as { productId?: unknown }).productId);
  return pid === 0x8803 || pid === 0xfefc;
}

/** M98 屏幕版 / M98 8K：有线与 2.4G 均支持神光同步、全局律动 */
export function isM98GlobalRhythmSyncCapableEquipment(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  return isM98ScreenTftEquipment(equipment) || isM98_8KEquipment(equipment);
}

export function matchHuafendaEquipmentToConfigRow(
  equipment: Record<string, unknown>,
  row: Record<string, unknown>
): boolean {
  const blob = equipmentNameBlob(equipment);
  const rowDn = normalizeEquipmentName(row.displayname);
  const rowPn = normalizeEquipmentName(row.productName);
  const rowIdent = normalizeEquipmentName(row.identifier);
  if (!blob) return false;
  if (rowDn && huafendaNameAppearsInBlob(blob, rowDn)) return true;
  if (rowPn && huafendaNameAppearsInBlob(blob, rowPn)) return true;
  if (rowIdent) {
    const tail = rowIdent.split(":").pop() ?? rowIdent;
    if (tail && huafendaNameAppearsInBlob(blob, tail)) return true;
  }
  return false;
}

export const normHuafendaIdentifier = (value: unknown): string =>
  typeof value === "string" ? value.trim() : String(value ?? "").trim();

/**
 * 华奋达 identifier 包含匹配（勿用严格相等）。
 * SDK getDeviceId 常在配置裸 id 后追加 `:workMode:product`，例如：
 * `3141:65273:2.4G Dongle:2:18` 包含 `3141:65273:2.4G Dongle`。
 */
export function huafendaIdentifiersCompatible(a: unknown, b: unknown): boolean {
  const left = normHuafendaIdentifier(a);
  const right = normHuafendaIdentifier(b);
  if (!left || !right) return false;
  if (left === right) return true;
  return left.includes(right) || right.includes(left);
}

/**
 * 配置行 identifier 是否命中 SDK deviceId（包含匹配）。
 * 多行命中时由调用方按更长 identifier 优先消歧。
 */
export function huafendaConfigIdentifierMatchesDeviceId(
  configIdentifier: unknown,
  deviceId: unknown
): boolean {
  const rowId = normHuafendaIdentifier(configIdentifier);
  const sdkId = normHuafendaIdentifier(deviceId);
  if (!rowId || !sdkId) return false;
  return sdkId === rowId || sdkId.includes(rowId);
}

function isHuafenda24GConfigRow(row: Record<string, unknown>): boolean {
  return row.is4G === true || row.workMode === 2 || row.workMode === "2";
}

/**
 * 型号名出现在 blob 中才算命中。
 */
function huafendaNameAppearsInBlob(blob: string, name: string): boolean {
  if (!name || !blob) return false;
  if (blob === name) return true;
  if (!blob.includes(name)) return false;
  // 「X87」是「X87 V2 / X87 Neon」的前缀，不能把后者命中到经典 X87 配置行
  if (name === "x87" && /\bx87\s*(?:v2|neon)\b/.test(blob)) return false;
  const escaped = name
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/\s+/g, "\\s+");
  return new RegExp(`(?:^|[\\s:])${escaped}(?=[\\s:]|$)`).test(blob);
}

/**
 * 2.4G 配置行：优先 identifier 精确命中，其次名称匹配；禁止 hits[0] 盲兜底串台。
 */
export function resolveHuafendaWirelessConfigRowForEquipment(
  equipment: Record<string, unknown>,
  keyboardType: string
): Record<string, unknown> | null {
  const anchorId = normHuafendaIdentifier(equipment.identifier);
  const rows = (
    getSupportedDevicesSync().keyboard as Array<Record<string, unknown>>
  ).filter(
    (row) => row.solutionRovider === "huafenda" && isHuafenda24GConfigRow(row)
  );

  if (anchorId) {
    // 包含匹配（SDK 全量 id ⊇ 配置裸 id）；多命中取更长
    const containHits = rows
      .filter((row) =>
        huafendaConfigIdentifierMatchesDeviceId(row.identifier, anchorId)
      )
      .sort(
        (a, b) =>
          normHuafendaIdentifier(b.identifier).length -
          normHuafendaIdentifier(a.identifier).length
      );
    if (containHits[0]) return containHits[0];
  }

  const kt = String(keyboardType ?? "").trim();
  const wirelessHits = rows.filter(
    (row) =>
      String(row.keyboardType ?? "") === kt &&
      matchHuafendaEquipmentToConfigRow(equipment, row)
  );
  if (wirelessHits.length === 1) return wirelessHits[0];
  if (wirelessHits.length > 1 && anchorId) {
    const byAnchor = wirelessHits
      .filter((row) => huafendaIdentifiersCompatible(row.identifier, anchorId))
      .sort(
        (a, b) =>
          normHuafendaIdentifier(b.identifier).length -
          normHuafendaIdentifier(a.identifier).length
      );
    if (byAnchor[0]) return byAnchor[0];
  }
  return wirelessHits[0] ?? null;
}

/** 按 SDK identifier 命中 supportedDevices 华奋达行（同 PID 多型号时以表为准） */
export function resolveHuafendaConfigRowForEquipment(
  equipment: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!equipment) return null;
  const id = normHuafendaIdentifier(equipment.identifier);
  if (!id) return null;

  const workModeHint = equipment.workMode;
  const product = (equipment as { product?: unknown }).product;
  const rows = (
    getSupportedDevicesSync().keyboard as Array<Record<string, unknown>>
  ).filter((row) => row.solutionRovider === "huafenda");

  const pick = (
    hits: Record<string, unknown>[]
  ): Record<string, unknown> | null => {
    if (hits.length === 0) return null;
    if (hits.length === 1) return hits[0];
    if (product != null && product !== "") {
      const prod = String(product);
      // X75 Screen 等：identifier 以 :2:product 结尾，优先于 M98 的 row.product 数字
      const byIdSuffix = hits.find((row) => {
        const ri = normHuafendaIdentifier(row.identifier);
        return (
          ri.endsWith(`:2:${prod}`) ||
          ri.endsWith(`:0:${prod}`) ||
          ri.endsWith(`:${prod}`)
        );
      });
      if (byIdSuffix) return byIdSuffix;
      // 仅同 identifier 多行（M98 基础/屏幕）才用 row.product
      const groups = new Map<string, Record<string, unknown>[]>();
      for (const row of hits) {
        const key = normHuafendaIdentifier(row.identifier).toLowerCase();
        const list = groups.get(key) ?? [];
        list.push(row);
        groups.set(key, list);
      }
      for (const group of groups.values()) {
        if (group.length < 2) continue;
        const byProduct = group.find((row) => row.product == product);
        if (byProduct) return byProduct;
      }
    }
    if (
      workModeHint !== undefined &&
      workModeHint !== null &&
      workModeHint !== ""
    ) {
      const wm = String(workModeHint);
      const byWm = hits.filter((row) => String(row.workMode ?? "") === wm);
      if (byWm.length === 1) return byWm[0];
      if (byWm.length > 1) {
        const named = byWm.find((row) =>
          matchHuafendaEquipmentToConfigRow(equipment, row)
        );
        if (named) return named;
        return byWm[0];
      }
    }
    const named = hits.find((row) =>
      matchHuafendaEquipmentToConfigRow(equipment, row)
    );
    return named ?? hits[0];
  };

  // 精确命中优先
  const exactHits = rows.filter(
    (row) =>
      normHuafendaIdentifier(row.identifier).toLowerCase() === id.toLowerCase()
  );
  if (exactHits.length) {
    return pick(exactHits);
  }

  // 包含匹配优先（勿严格相等）：SDK `…Dongle:2:18` 命中配置 `…Dongle`
  const containHits = rows
    .filter((row) =>
      huafendaConfigIdentifierMatchesDeviceId(row.identifier, id)
    )
    .sort(
      (a, b) =>
        normHuafendaIdentifier(b.identifier).length -
        normHuafendaIdentifier(a.identifier).length
    );
  const containPick = pick(containHits);
  if (containPick) return containPick;

  const idLower = id.toLowerCase();

  const tail = idLower.split(":").pop() ?? "";
  if (tail) {
    const suffixHits = rows.filter((row) => {
      const ri = normHuafendaIdentifier(row.identifier).toLowerCase();
      return ri.endsWith(`:${tail}`) || ri.includes(tail);
    });
    const suffixPick = pick(suffixHits);
    if (suffixPick) return suffixPick;
  }

  return null;
}

/**
 * 按卡片 displayname / 型号名 + 有线/2.4G 命中配置行。
 * 共用 dongle 时 SDK identifier 可能仍是 X87，须优先于 identifier 解析。
 */
export function resolveHuafendaConfigRowByNameAndWorkMode(
  equipment: Record<string, unknown>
): Record<string, unknown> | null {
  const fromNameKt = resolveHuafendaKeyboardTypeFromNameBlob(equipment);
  if (!fromNameKt) return null;

  const wm = equipment.workMode;
  const on24G =
    wm === 2 ||
    wm === "2" ||
    equipment.is4G === true ||
    (typeof globalThis !== "undefined" &&
      typeof localStorage !== "undefined" &&
      localStorage.getItem("huafenda2.4G") === "true");

  const rows = (
    getSupportedDevicesSync().keyboard as Array<Record<string, unknown>>
  ).filter(
    (row) =>
      row.solutionRovider === "huafenda" &&
      String(row.keyboardType ?? "") === fromNameKt &&
      (on24G ? isHuafenda24GConfigRow(row) : !isHuafenda24GConfigRow(row))
  );

  if (rows.length === 0) return null;

  const named = rows.filter((row) =>
    matchHuafendaEquipmentToConfigRow(equipment, row)
  );
  if (named.length >= 1) {
    // 短名（X87）与长名（X87 Neon）同时命中时取更长的型号名
    named.sort((a, b) => {
      const score = (row: Record<string, unknown>) =>
        Math.max(
          normalizeEquipmentName(row.displayname).length,
          normalizeEquipmentName(row.productName).length,
          normalizeEquipmentName(row.identifier).length
        );
      return score(b) - score(a);
    });
    return named[0];
  }

  const cardDn = normalizeEquipmentName(
    equipment.displayname ?? equipment.displayName
  );
  if (cardDn) {
    const byDn = rows.find(
      (row) => normalizeEquipmentName(row.displayname) === cardDn
    );
    if (byDn) return byDn;
  }

  return rows.length === 1 ? rows[0] : null;
}

/** 华奋达：从 displayname / identifier 等解析型号（优先于错误的 keyboardType 字段） */
export function resolveHuafendaKeyboardTypeFromNameBlob(
  equipment: Record<string, unknown> | null | undefined
): string | null {
  if (!equipment || String(equipment.solutionRovider ?? "") !== "huafenda") {
    return null;
  }
  const blob = equipmentNameBlob(equipment);
  if (!blob) return null;
  const u = blob.toUpperCase();
  if (u.includes("SMART875")) return "84";
  if (/\bDEEP80\s*MAX\b/i.test(blob)) return "deep80max";
  if (u.includes("DK108")) return "DK108";
  // 华奋达 DK63Star：布局对齐星闪 DK63 V2（keyboardType 63，单灯）
  if (u.includes("DK63STAR") || u.includes("DK63 STAR")) return "63";
  // M98 8K 须先于 M98 / MUSE-M98，避免 displayname「M98 (8K)」或 MUSE-M98-8K 误落 M98
  if (
    u.includes("M98 (8K)") ||
    u.includes("M98 8K") ||
    u.includes("MUSE-M98-8K")
  ) {
    return "M98 8K";
  }
  if (u.includes("M98 SCREEN") || u.includes("M98TFT")) return "M98TFT";
  if (u.includes("MUSE-M98") || /\bM98\b/.test(u)) return "M98";
  if (u.includes("X75 PRO SCREEN") || u.includes("75PROTFT")) return "75ProTFT";
  if (u.includes("X75 PRO")) return "75Pro";
  if (u.includes("X75GC")) return "x75gc";
  if (u.includes("X87 V2")) return "huafenda87";
  if (/\bX87\b/.test(u)) return "huafenda87";
  if (u.includes("TANK68")) return "tank68he";
  if (
    u.includes("BATTLE68") ||
    u.includes("BAT68") ||
    u.includes("HUAFENDA68")
  ) {
    return "huafenda68";
  }
  return null;
}

const HFD_SHARED_DONGLE_PIDS = new Set([0xfefe, 0xfef9, 0xfefa]);

/**
 * 从 supportedDevices 解析 keyboardType（华奋达等同 VID/PID 时按名称区分 X87 / SMART875 等）。
 */
export function resolveSupportedKeyboardTypeForEquipment(
  equipment: Record<string, unknown> | null | undefined
): string | null {
  if (!equipment || typeof equipment !== "object") return null;

  const vendorId = Number(equipment.vendorId);
  const productId = Number(equipment.productId);
  const hits =
    Number.isFinite(vendorId) && Number.isFinite(productId)
      ? (
          getSupportedDevicesSync().keyboard as Array<Record<string, unknown>>
        ).filter(
          (row) =>
            row.isSupported !== false &&
            Number(row.vendorId) === vendorId &&
            (typeof row.productId !== "number" ||
              Number(row.productId) === productId)
        )
      : [];

  const fromName = resolveHuafendaKeyboardTypeFromNameBlob(equipment);
  const sharedDongle = HFD_SHARED_DONGLE_PIDS.has(productId) || hits.length > 1;

  if (
    fromName &&
    (sharedDongle || String(equipment.solutionRovider) === "huafenda")
  ) {
    return fromName;
  }

  const fromField = String(
    equipment.keyboardType ??
      (equipment as { keyboard_type?: unknown }).keyboard_type ??
      ""
  ).trim();
  if (fromField && fromField !== "undefined" && fromField !== "null") {
    if (fromName && fromField !== fromName && sharedDongle) {
      return fromName;
    }
    return fromField;
  }

  if (fromName) return fromName;

  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) {
    return null;
  }

  if (hits.length === 0) return null;
  if (hits.length === 1) {
    return String(hits[0].keyboardType ?? "") || null;
  }

  const huafendaHits = hits.filter(
    (r) => String(r.solutionRovider ?? "") === "huafenda"
  );
  if (huafendaHits.length > 0) {
    const named = huafendaHits.find((row) =>
      matchHuafendaEquipmentToConfigRow(equipment, row)
    );
    if (named?.keyboardType) return String(named.keyboardType);
  }

  const namedAny = hits.find((row) =>
    matchHuafendaEquipmentToConfigRow(equipment, row)
  );
  if (namedAny?.keyboardType) return String(namedAny.keyboardType);

  return String(hits[0].keyboardType ?? "") || null;
}

/**
 * 设备是否命中配置表某一行（与 `isEquipmentInSupportedDevicesConfig` 内规则一致）。
 */
export function keyboardCatalogRowMatchesEquipment(
  row: Record<string, unknown>,
  equipment: Record<string, unknown>
): boolean {
  const vendorId = Number(equipment.vendorId);
  const productId = Number(equipment.productId);
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) {
    return false;
  }
  if (row.isSupported === false) return false;
  if (Number(row.vendorId) !== vendorId) return false;
  if (
    typeof row.productId === "number" &&
    Number(row.productId) !== productId
  ) {
    return false;
  }

  const itemPn = normalizeEquipmentName(equipment.productName);
  const solution = String(row.solutionRovider ?? "");
  const freqFamilyProductNameMatch =
    solution === "freq" || solution === "freqC"
      ? normalizeEquipmentName(row.productName) &&
        itemPn &&
        normalizeEquipmentName(row.productName) === itemPn
      : true;
  const xingshanProductNameMatch =
    solution !== "xingshan" || xingshanHidMatchesConfiguration(row, equipment);
  const beiyingProductNameMatch =
    solution === "beiying" &&
    (row.keyboardType === "m80" || row.keyboardType === "m71")
      ? !itemPn ||
        (Boolean(normalizeEquipmentName(row.productName)) &&
          normalizeEquipmentName(row.productName) === itemPn)
      : true;
  const huafendaNameMatch =
    solution === "huafenda"
      ? matchHuafendaEquipmentToConfigRow(equipment, row)
      : true;

  return (
    freqFamilyProductNameMatch &&
    xingshanProductNameMatch &&
    beiyingProductNameMatch &&
    huafendaNameMatch
  );
}

/** 配置表 / 实机 uuid 主值归一化，供同 VID+PID 多行消歧 */
function readPrimaryUuidToken(value: unknown): string | null {
  if (value == null) return null;
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw == null || raw === "") return null;
  const asNum = Number(raw);
  if (Number.isFinite(asNum)) return String(asNum);
  const asStr = String(raw).trim();
  return asStr || null;
}

/**
 * 按实机/快照字段在 keyboard 配置表中定位唯一一行（连接页同款规则，不用 displayname 消歧）。
 * ES98 2.4G 等同 VID+PID 可能有多条重复表项：再用 productName / uuid 收窄；
 * 若剩余行 uuid 完全相同则视为同型号重复配置，取首条（避免神光/律动列表整项剔除）。
 */
export function resolveKeyboardCatalogRowForEquipment(
  equipment: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!equipment || typeof equipment !== "object") return null;
  const vendorId = Number(equipment.vendorId);
  const productId = Number(equipment.productId);
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) {
    return null;
  }

  const matches = (
    getSupportedDevicesSync().keyboard as Array<Record<string, unknown>>
  ).filter((row) => keyboardCatalogRowMatchesEquipment(row, equipment));

  if (matches.length === 1) return matches[0];
  if (!matches.length) return null;

  let candidates = matches;

  const keyboardType = String(equipment.keyboardType ?? "").trim();
  if (keyboardType) {
    const byType = candidates.filter(
      (row) => String(row.keyboardType ?? "") === keyboardType
    );
    if (byType.length === 1) return byType[0];
    if (byType.length > 1) candidates = byType;
  }

  const solution = String(equipment.solutionRovider ?? "").trim();
  if (solution) {
    const bySolution = candidates.filter(
      (row) => String(row.solutionRovider ?? "") === solution
    );
    if (bySolution.length === 1) return bySolution[0];
    if (bySolution.length > 1) candidates = bySolution;
  }

  const itemPn = normalizeEquipmentName(equipment.productName);
  if (itemPn) {
    const byPn = candidates.filter(
      (row) => normalizeEquipmentName(row.productName) === itemPn
    );
    if (byPn.length === 1) return byPn[0];
    if (byPn.length > 1) candidates = byPn;
  }

  const eqUuid = readPrimaryUuidToken(equipment.uuid);
  if (eqUuid) {
    const byUuid = candidates.filter(
      (row) => readPrimaryUuidToken(row.uuid) === eqUuid
    );
    if (byUuid.length === 1) return byUuid[0];
    if (byUuid.length > 1) candidates = byUuid;
  }

  // 同 uuid 重复表项（如 ES98 两条 0x106c）：任取一条即可通过「是否在表内」
  const uuidTokens = new Set(
    candidates
      .map((row) => readPrimaryUuidToken(row.uuid))
      .filter((token): token is string => Boolean(token))
  );
  if (candidates.length > 1 && uuidTokens.size === 1) {
    return candidates[0];
  }

  return null;
}

/** 匠盟 mouse：从实机/configuration/deviceInfo 读取 cid/mid/type */
function readJiangmengMouseIdentity(equipment: Record<string, unknown>): {
  cid: number | null;
  mid: number | null;
  type: number | null;
} {
  const info =
    equipment.info && typeof equipment.info === "object"
      ? (equipment.info as Record<string, unknown>)
      : null;
  const deviceInfo =
    equipment.deviceInfo && typeof equipment.deviceInfo === "object"
      ? (equipment.deviceInfo as Record<string, unknown>)
      : null;

  const readNum = (...values: unknown[]): number | null => {
    for (const value of values) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) return num;
    }
    return null;
  };

  return {
    cid: readNum(
      equipment.customerId,
      equipment.cid,
      info?.cid,
      deviceInfo?.cid
    ),
    mid: readNum(equipment.moudleId, equipment.mid, info?.mid, deviceInfo?.mid),
    type: readNum(equipment.type, info?.type, deviceInfo?.type),
  };
}

export function mouseCatalogRowMatchesEquipment(
  row: Record<string, unknown>,
  equipment: Record<string, unknown>
): boolean {
  const vendorId = Number(equipment.vendorId);
  const productId = Number(equipment.productId);
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) {
    return false;
  }
  if (row.isSupported === false) return false;
  if (Number(row.vendorId) !== vendorId) return false;
  if (
    typeof row.productId === "number" &&
    Number(row.productId) !== productId
  ) {
    return false;
  }

  const solution = String(
    row.solutionRovider ?? equipment.solutionRovider ?? ""
  ).trim();

  if (solution === "jiangmeng") {
    const { cid, mid, type } = readJiangmengMouseIdentity(equipment);
    if (
      cid != null &&
      row.customerId != null &&
      Number(row.customerId) !== cid
    ) {
      return false;
    }
    if (mid != null && row.moudleId != null && Number(row.moudleId) !== mid) {
      return false;
    }
    if (type != null && row.type != null && Number(row.type) !== type) {
      return false;
    }
  }

  if (solution === "freq" || solution === "freqC") {
    const itemPn = normalizeEquipmentName(equipment.productName);
    const rowPn = normalizeEquipmentName(row.productName);
    if (itemPn && rowPn && itemPn !== rowPn) return false;
  }

  const deviceId = String(equipment.deviceId ?? "").trim();
  if (deviceId && row.deviceId && String(row.deviceId) !== deviceId) {
    return false;
  }

  return true;
}

/**
 * mouse 配置表唯一行。
 * 展立等同 VID/PID 多表项（如 Alpha7 2.4G：EXX Alpha7 / EXX 8K Dongle）
 * 需按 productName 消歧，与连接侧 matchZhanliConfig 对齐。
 */
export function resolveMouseCatalogRowForEquipment(
  equipment: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!equipment || typeof equipment !== "object") return null;
  const vendorId = Number(equipment.vendorId);
  const productId = Number(equipment.productId);
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) {
    return null;
  }

  const matches = (
    getSupportedDevicesSync().mouse as Array<Record<string, unknown>>
  ).filter((row) => mouseCatalogRowMatchesEquipment(row, equipment));

  if (matches.length === 1) return matches[0];
  if (!matches.length) return null;

  let candidates = matches;

  const deviceId = String(equipment.deviceId ?? "").trim();
  if (deviceId) {
    const byDeviceId = candidates.filter(
      (row) => String(row.deviceId ?? "") === deviceId
    );
    if (byDeviceId.length === 1) return byDeviceId[0];
    if (byDeviceId.length > 1) candidates = byDeviceId;
  }

  const displayname = String(
    equipment.displayname ?? equipment.displayName ?? ""
  ).trim();
  if (displayname) {
    const byDisplayname = candidates.filter(
      (row) => String(row.displayname ?? "") === displayname
    );
    if (byDisplayname.length === 1) return byDisplayname[0];
    if (byDisplayname.length > 1) candidates = byDisplayname;
  }

  const itemPn = normalizeEquipmentName(equipment.productName);
  if (itemPn) {
    const byPn = candidates.filter(
      (row) => normalizeEquipmentName(row.productName) === itemPn
    );
    if (byPn.length === 1) return byPn[0];
    if (byPn.length > 1) candidates = byPn;
  }

  return null;
}

/**
 * 设备是否命中 `supportedDevices.ts` 的 keyboard 配置表（isSupported !== false）。
 * 与主应用 `resolveKeyboardAdaptationForHid` 规则一致，供全局律动列表过滤「表外设备」。
 */
export function isEquipmentInSupportedDevicesConfig(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  return resolveKeyboardCatalogRowForEquipment(equipment) != null;
}

/**
 * 设备是否命中 `supportedDevices.ts` 的 mouse 配置表（isSupported !== false）。
 */
export function isMouseInSupportedDevicesConfig(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  return resolveMouseCatalogRowForEquipment(equipment) != null;
}

/** 键鼠均识别：供社区已连接设备列表等场景使用 */
export function isEquipmentInSupportedDevicesCatalog(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  return (
    isMouseInSupportedDevicesConfig(equipment) ||
    isEquipmentInSupportedDevicesConfig(equipment)
  );
}
